import useAuthStore from '@/stores/useAuthStore'
import usePermissionsStore from '@/stores/usePermissionsStore'
import useLogout from '@/hooks/useLogout'
import { accountMenu } from '@/app/nav'
import { canSeeMenu } from '@/components/Sidebar/useSidebarNav'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, LogOut, Sparkles } from 'lucide-react'
import { useAppStore } from '@/stores/useAppStore'
import { useReleaseNotes } from '@/components/WhatsNew/use-release-notes'
import {
    Avatar,
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    Text,
} from '@vnetwork/datacentral-ui/primitives'

/**
 * Topbar account menu: identity, the account pages (formerly the sidebar's
 * "Tài khoản" group) and sign out.
 */
const AccountDropdown = () => {
    const { t } = useTranslation()
    const { t: tNotes } = useTranslation('release-notes')
    const toggleSidePanel = useAppStore((state) => state.toggleSidePanel)
    const { notes, unreadCount } = useReleaseNotes()
    const navigate = useNavigate()
    const { employee } = useAuthStore()
    const { permissions, isAdmin } = usePermissionsStore()
    const { handleLogout, isPending } = useLogout()

    if (!employee) return null

    const displayName = employee.employee_name || 'User'
    const subtitle = [employee.position_name, employee.department_name]
        .filter(Boolean)
        .join(' • ')

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    className="gap-2 px-1.5"
                    aria-label={t('account')}
                    data-tour="account-menu"
                    data-tour-links={JSON.stringify(
                        Object.fromEntries(
                            accountMenu
                                .filter(({ item }) =>
                                    canSeeMenu(item, permissions, isAdmin, true)
                                )
                                .map(({ item }) => [item.title, item.path])
                        )
                    )}
                >
                    <Avatar name={displayName} size="sm" />
                    <Text
                        as="span"
                        size="sm"
                        weight="medium"
                        truncate
                        className="hidden max-w-40 md:block"
                    >
                        {displayName}
                    </Text>
                    <ChevronDown
                        size={14}
                        className="hidden shrink-0 text-muted-foreground md:block"
                    />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
                <div className="space-y-0.5 px-3 py-2">
                    <Text as="div" size="sm" weight="medium" truncate>
                        {displayName}
                    </Text>
                    {subtitle && (
                        <Text as="div" size="xs" tone="muted" truncate>
                            {subtitle}
                        </Text>
                    )}
                    <Text as="div" size="xs" tone="subtle" truncate>
                        {employee.employee_email_company}
                    </Text>
                </div>
                <DropdownMenuSeparator />
                {accountMenu
                    .filter(({ item }) =>
                        canSeeMenu(item, permissions, isAdmin, true)
                    )
                    .map(({ item, icon }) => (
                        <DropdownMenuItem
                            key={item.path}
                            onSelect={() => navigate(item.path)}
                        >
                            {icon}
                            {t(item.title)}
                        </DropdownMenuItem>
                    ))}
                {notes.length > 0 && (
                    // Below md only: the topbar button is hidden there.
                    <DropdownMenuItem
                        className="md:hidden"
                        onSelect={() => toggleSidePanel('whats-new')}
                    >
                        <Sparkles size={16} />
                        {tNotes('whats-new.title')}
                        {unreadCount > 0 && (
                            <Text
                                as="span"
                                size="xs"
                                tone="muted"
                                className="ml-auto"
                            >
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </Text>
                        )}
                    </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    disabled={isPending}
                    onSelect={() => handleLogout()}
                >
                    <LogOut size={16} />
                    {isPending ? t('logging_out') : t('logout')}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

export default AccountDropdown
